import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { 
  Plug, 
  Brain, 
  Zap, 
  FileSearch, 
  Shield,
  Share2,
  ArrowUpRight
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { NeuralNetwork } from './NeuralNetwork';

const icons = [Plug, Brain, Zap, FileSearch, Share2, Shield];

const FeatureCard = ({ feature, index, isInView, Icon }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`feature-card-${index + 1}`}
      className={`relative group ${index === 1 ? 'md:col-span-2 md:row-span-2' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`relative h-full rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden transition-all duration-300 ${
          isHovered ? 'border-zinc-300 shadow-lg' : 'shadow-sm'
        }`}
      >
        <div className={`relative z-10 p-6 h-full flex flex-col ${index === 1 ? 'justify-between' : ''}`}>
          {/* Top section */}
          <div>
            {/* Icon and arrow */}
            <div className="flex items-start justify-between mb-5">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                index === 1 ? 'bg-blue-500' : 'bg-zinc-100'
              }`}>
                <Icon className={`w-5 h-5 ${index === 1 ? 'text-white' : 'text-zinc-400'}`} />
              </div>
              
              <motion.div
                className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                whileHover={{ scale: 1.1 }}
              >
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400" />
              </motion.div>
            </div>

            {/* Subtitle */}
            <span className={`text-xs font-semibold tracking-wider uppercase ${
              index === 1 ? 'text-blue-500' : 'text-zinc-400'
            }`}>
              {feature.subtitle}
            </span>

            {/* Title */}
            <h3 className="text-lg sm:text-xl font-semibold text-zinc-100 mt-1.5 mb-3 tracking-tight">
              {feature.title}
            </h3>

            {/* Description */}
            <p className="text-sm text-zinc-400 leading-relaxed">
              {feature.description}
            </p>
          </div>

          {/* Neural Network Animation for Unified Intelligence */}
          {index === 1 && (
            <div className="my-4 flex-grow min-h-[200px]">
              <NeuralNetwork />
            </div>
          )}

          {/* Stats */}
          <div className={`mt-5 pt-5 border-t border-zinc-800 flex gap-6 ${index === 1 ? 'mt-auto' : ''}`}>
            {feature.stats.map((stat, i) => (
              <div key={i}>
                <div className={`text-xl font-semibold ${index === 1 ? 'text-blue-500' : 'text-zinc-100'}`}>
                  {stat.value}
                </div>
                <div className="text-xs text-zinc-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const data = t('features');

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-zinc-900"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 block">
            {data.eyebrow}
          </span>
          <h2
            data-testid="features-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
          >
            {data.headline}{' '}
            <span className="font-semibold gradient-text">
              {data.headlineHighlight}
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            {data.subheadline}
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((feature, index) => (
            <FeatureCard
              key={index}
              feature={feature}
              index={index}
              isInView={isInView}
              Icon={icons[index]}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
